import { createContext, useCallback, useEffect, useState } from "react";
//#region src/components/ThemeProvider/utils.ts
const createTheme = (brand) => ({
	id: brand.id,
	name: brand.name,
	shell: brand.shell,
	logo: brand.logo
});
const createCssVars = () => {
	const vars = {};
	return {
		set: (name, value) => {
			if (value !== void 0) vars[name] = value;
		},
		expose: () => ({ ...vars })
	};
};
//#endregion
//#region src/components/ThemeProvider/defaults.ts
const DEFAULT_COLOR_SCHEME = "light";
const DEFAULT_BRAND_ID = "design-system";
const defaultBrand = {
	id: DEFAULT_BRAND_ID,
	name: "Design System",
	shell: "sidebar",
	theme: {}
};
const defaultTheme = createTheme(defaultBrand);
const DEFAULT_BRANDS = [];
//#endregion
//#region src/components/ThemeProvider/useBrandState.ts
const BRAND_ATTRIBUTE = "data-brand";
const useBrandState = ({ defaultBrandId: defaultBrand = DEFAULT_BRAND_ID, brandId: controlledBrand, onBrandIdChange: onBrandChange } = {}) => {
	const [uncontrolledBrand, setUncontrolledBrand] = useState(defaultBrand);
	const isControlled = controlledBrand !== void 0;
	const brandId = isControlled ? controlledBrand : uncontrolledBrand;
	const setBrand = useCallback((next) => {
		if (!isControlled) setUncontrolledBrand(next);
		onBrandChange?.(next);
	}, [isControlled, onBrandChange]);
	useEffect(() => {
		if (typeof document === "undefined") return;
		document.documentElement.setAttribute(BRAND_ATTRIBUTE, brandId);
	}, [brandId]);
	return {
		brandId,
		setBrand
	};
};
//#endregion
//#region src/components/ThemeProvider/useColorSchemeState.ts
const DARK_QUERY = "(prefers-color-scheme: dark)";
const THEME_ATTRIBUTE = "data-theme";
const getSystemColorScheme = () => {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
	return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
};
const useColorSchemeState = ({ defaultScheme = DEFAULT_COLOR_SCHEME, scheme: propScheme, onSchemeChange } = {}) => {
	const [uncontrolledScheme, setUncontrolledScheme] = useState(defaultScheme);
	const [systemScheme, setSystemScheme] = useState(getSystemColorScheme);
	const isControlled = propScheme !== void 0;
	const scheme = isControlled ? propScheme : uncontrolledScheme;
	const setScheme = useCallback((next) => {
		if (!isControlled) setUncontrolledScheme(next);
		onSchemeChange?.(next);
	}, [isControlled, onSchemeChange]);
	useEffect(() => {
		if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
		const mq = window.matchMedia(DARK_QUERY);
		const onChange = () => setSystemScheme(mq.matches ? "dark" : "light");
		onChange();
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);
	const resolvedScheme = scheme === "system" ? systemScheme : scheme;
	useEffect(() => {
		if (typeof document === "undefined") return;
		document.documentElement.setAttribute(THEME_ATTRIBUTE, resolvedScheme);
	}, [resolvedScheme]);
	return {
		scheme,
		resolvedScheme,
		setScheme
	};
};
//#endregion
//#region src/components/ThemeProvider/themeScopeContext.ts
/**
* What a `ThemeScope` publishes about itself, in its own module so a hook can read it without
* importing the component that provides it.
*
* - `scheme`: the scheme this subtree is pinned to, or `null` outside a scope.
* - `container`: a body-level node carrying the scope's `data-theme` and token vars. A canvas
*   (chart, diagram) that resolves live `--*` values reads them off this rather than `<html>`,
*   which is still painting the page's scheme.
*/
const ThemeScopeSchemeContext = createContext(null);
const ThemeScopeContainerContext = createContext(null);
//#endregion
export { BRAND_ATTRIBUTE, DEFAULT_BRANDS, DEFAULT_BRAND_ID, DEFAULT_COLOR_SCHEME, THEME_ATTRIBUTE, ThemeScopeContainerContext, ThemeScopeSchemeContext, createCssVars, createTheme, defaultBrand, defaultTheme, useBrandState, useColorSchemeState };

//# sourceMappingURL=themeScopeContext-CEVXbYvZ.js.map